import { Loading } from '@wfp/react';
import React, { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  DisplayWrapper,
  mapStateToWrapperProps as DisplayWrapperMapStateToProps,
  SHOW,
} from 'src/components/DisplayWrapper';
import NoAccess from 'src/components/NoAccess';
import { resolveCrf } from 'src/crf';
import { sendSignalsAction } from 'src/redux/componentSignals/actions';
import {
  COMMENTS_TARGET_ID,
  REPORT_DETAIL_DATA_KEY,
} from 'src/redux/constants';
import type { RootState } from 'src/redux/store';
import {
  changeViewStateAction,
  clearViewData,
  clearViewState,
  selectSection,
} from 'src/redux/viewState/actions';
import type { IReportData } from 'src/types';
import { buildErrorComponents, showNotification } from 'src/utils';
import CommentPanel from './comments/CommentPanel';
import ReportHeader from './ReportHeader';
import ReportSidebar from './ReportSidebar';
import Workspace from './Workspace';
import WorkspaceACR2023 from './WorkspaceACR2023';

const Detail = () => {
  const hasProcessedInitialUrl = useRef(false);

  const reportDetailData = useSelector(
    (state: RootState): IReportData | undefined =>
      state.viewData[REPORT_DETAIL_DATA_KEY]?.data,
  );
  const reportDetailLoading = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY]?.loading,
  );
  const reportDetailError = useSelector(
    (state: RootState) => state.viewData[REPORT_DETAIL_DATA_KEY]?.error,
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const params = useParams();

  const commentsDisplayStatus = useSelector(
    (state: RootState) =>
      DisplayWrapperMapStateToProps(state, {
        targetId: COMMENTS_TARGET_ID,
      }).display,
  );

  const activeSectionID = useSelector(
    (state: RootState) => state.viewState.activeSectionID,
  );

  const getCommentIdFromUrl = () => {
    const fullUrl = window.location.href;
    const structureMatch = fullUrl.match(/#[^?]*\?(?:comment|reply)=(\d+)/);
    return structureMatch ? structureMatch[1] : null;
  };

  useEffect(() => {
    const commentId = getCommentIdFromUrl();

    if (
      commentId &&
      !hasProcessedInitialUrl.current &&
      reportDetailData &&
      !reportDetailLoading
    ) {
      hasProcessedInitialUrl.current = true;

      dispatch(
        changeViewStateAction({
          highlightComment: commentId,
        }),
      );

      dispatch(
        sendSignalsAction({
          [COMMENTS_TARGET_ID]: { [SHOW]: true },
        }),
      );
    }
  }, [reportDetailData, reportDetailLoading]);

  useEffect(() => {
    if (!reportDetailData) return;

    const hashContent = location.hash.substring(1);

    const queryIndex = hashContent.indexOf('?');
    const sectionAnchor =
      queryIndex !== -1 ? hashContent.substring(0, queryIndex) : hashContent;

    if (
      sectionAnchor &&
      !activeSectionID &&
      reportDetailData.sections &&
      !sectionAnchor.match(/^(?:comment|reply)-\d+$/)
    ) {
      const sectionTitle = decodeURIComponent(sectionAnchor);

      const findSectionByTitle = (
        sections: any[],
        targetTitle: string,
      ): any => {
        for (const section of sections) {
          const title = section.current_title || section.custom_title;
          if (title === targetTitle) {
            return section;
          }
          if (section.children_list) {
            const found = findSectionByTitle(
              section.children_list,
              targetTitle,
            );
            if (found) return found;
          }
        }
        return null;
      };

      const foundSection = findSectionByTitle(
        reportDetailData.sections,
        sectionTitle,
      );
      if (foundSection) {
        dispatch(selectSection(foundSection.section_pk));
      }
    }
  }, [reportDetailData, activeSectionID, location.hash]);

  useEffect(() => {
    hasProcessedInitialUrl.current = false;
  }, [location.pathname]);

  const countryDirector = useMemo(() => {
    return reportDetailData?.country_director_name || '';
  }, [reportDetailData]);

  const regionalDirector = useMemo(() => {
    return reportDetailData?.regional_director_name || '';
  }, [reportDetailData]);

  useEffect(() => {
    if (!reportDetailError) return;

    const errorsList = buildErrorComponents(reportDetailError);
    showNotification(errorsList as unknown as React.JSX.Element, 'error', {
      autoClose: false,
      closeOnClick: false,
    });
  }, [reportDetailError]);

  useEffect(() => {
    return () => {
      dispatch(clearViewState());
      dispatch(clearViewData());
    };
  }, []);

  if (
    reportDetailError?.data?.detail === 'Not found.' ||
    reportDetailError?.data?.detail === 'No Report matches the given query.'
  ) {
    navigate('/not-found');
  }

  const hasPermissionToViewReport =
    reportDetailData?.permissions?.has_permission_to_view_report;

  if (reportDetailData && hasPermissionToViewReport === false) {
    return <NoAccess />;
  }

  return (
    <div
      className={`report-details ${reportDetailError ? 'request-error' : ''} ${
        commentsDisplayStatus ? 'comments-view' : ''
      }`}
    >
      {reportDetailData && (
        <ReportHeader
          country={reportDetailData.country}
          period={reportDetailData.period}
          project_type={reportDetailData.project_type}
          project_code={reportDetailData.project_code}
          project_title={reportDetailData.title}
          country_iso_code={reportDetailData.country_iso_code}
          has_director={reportDetailData.has_director}
          has_country_overview={reportDetailData.has_country_overview}
          report_pk={reportDetailData.pk}
          contact_name={reportDetailData.contact_name}
          contact_email={reportDetailData.contact_email}
          regional_director_name={regionalDirector}
          country_director_name={countryDirector}
          photo_credit={reportDetailData.photo_credit}
          caption={reportDetailData.caption}
        />
      )}
      {!reportDetailData ? (
        <main className="section-detail">
          <div className="section-loader-wrapper">
            <Loading className="section-loader" withOverlay={false} active />
          </div>
        </main>
      ) : resolveCrf(reportDetailData.crf).workspaceVariant === 'legacy' ? (
        <Workspace />
      ) : (
        <WorkspaceACR2023 />
      )}

      <ReportSidebar year={params.year} cpb={params.cpb} />
      <div className="acr-comments-panel-container">
        <DisplayWrapper targetId={COMMENTS_TARGET_ID} visible={false}>
          <CommentPanel
            id="comments_panel"
            reportPk={reportDetailData?.pk}
            officePk={reportDetailData?.office?.pk}
            sectionPk={activeSectionID}
          />
        </DisplayWrapper>
      </div>
    </div>
  );
};

export default Detail;
